import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import PageTitle from 'components/headers/PageTitle';
import {
  useCurrentPeriod,
  usePeriods,
} from 'components/IAP/hooks/incidentHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';
import ApprovedFormsPeriodHeader from 'components/IAP/planningView/ApprovedFormsPeriodHeader';
import { APPROVED_FORMS_IAP_WIDGET } from 'components/IAP/planningView/approvedFormOptions';
import { useApprovedFormsForPeriod } from 'components/IAP/planningView/useApprovedFormsForPeriod';
import { isPremiumResourceWidget } from 'components/IAP/premiumResourceManagement';
import {
  FormsDropdown,
  RenderWidget,
  useApprovedWidgetRenderContext,
} from 'components/IAP/planningView/widgetRegistry';
import { parseFormsViewParam, serializeFormsView } from 'utils/iapFormsView';
import { usePremiumResourceManagementFlag } from 'utils/featureFlags';

/**
 * Read-only current / historical OP packages. Editable Working OP stays on
 * Planning-P.
 *
 * The period picker chooses which package, and the Forms picker under it
 * chooses which form within that package. Only forms the period actually holds
 * are offered, alongside the IAP that collates them.
 */
const ApprovedIcsFormsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: periods = [] } = usePeriods();
  const { periodNumber } = useCurrentPeriod();
  const currentPeriodNumber = periodNumber ?? 0;
  const { setFormsView, formsView } = useICSContext();
  const formsViewParam = searchParams.get('formsView');

  const selectedPeriod =
    typeof formsView === 'number' ? formsView : currentPeriodNumber;

  const premiumResourceManagement = usePremiumResourceManagementFlag();
  const formOptions = useApprovedFormsForPeriod(selectedPeriod).filter(
    (option) =>
      premiumResourceManagement || !isPremiumResourceWidget(option.value)
  );
  const cardParam = searchParams.get('card');

  /*
   * Derived rather than stored, so a period with no version of the selected form falls back to the IAP without
   * rewriting the URL. The param survives the switch, and the form reappears on returning to a period that has it.
   */
  const card = formOptions.some((option) => option.value === cardParam)
    ? (cardParam as string)
    : APPROVED_FORMS_IAP_WIDGET;

  const widgetContext = useApprovedWidgetRenderContext(
    periods.find((period) => period.period_number === selectedPeriod),
    selectedPeriod
  );

  useEffect(() => {
    const fromUrl = parseFormsViewParam(formsViewParam);
    const next = typeof fromUrl === 'number' ? fromUrl : currentPeriodNumber;
    setFormsView(next);

    if (formsViewParam !== serializeFormsView(next)) {
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          nextParams.set('formsView', serializeFormsView(next));
          return nextParams;
        },
        { replace: true }
      );
    }
  }, [formsViewParam, currentPeriodNumber, setFormsView, setSearchParams]);

  const onPeriodChange = (period: number) => {
    setFormsView(period);
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.set('formsView', serializeFormsView(period));
        return nextParams;
      },
      { replace: true }
    );
  };

  const onCardChange = (next: string) => {
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.set('card', next);
        return nextParams;
      },
      { replace: true }
    );
  };

  return (
    <div
      className="d-flex flex-column w-100"
      data-testid="approved-ics-forms-page"
    >
      <PageTitle title="Approved ICS Forms" parents={['Incident Management']} />
      <ApprovedFormsPeriodHeader
        periods={periods}
        selectedPeriodNumber={selectedPeriod}
        onPeriodChange={onPeriodChange}
      />
      <div
        className="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:border-b tw:border-border tw:bg-black tw:px-3 tw:py-2"
        data-testid="approved-forms-picker"
      >
        <FormsDropdown
          options={formOptions}
          value={card}
          onChange={onCardChange}
          id="approved-forms-dropdown"
        />
      </div>
      <div className="maincontent">
        <div className="ps-3">
          <div className="planning-container">
            <RenderWidget widgetName={card} ctx={widgetContext} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovedIcsFormsPage;
